import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Building2, Plus, Pencil, Trash2, Check, X } from 'lucide-react';

/**
 * AdminCarrierMaster — admin-managed master list of insurance carriers that
 * Utmost Financial Inc. holds contracts with. Appears on every agent portal.
 */
const AdminCarrierMaster = ({ apiUrl, adminPassword }) => {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = { 'Content-Type': 'application/json', 'X-Admin-Password': adminPassword };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/compliance/reference/carriers/admin`, { headers });
      if (!res.ok) throw new Error('Failed to load carriers');
      setCarriers(await res.json());
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, adminPassword]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const addCarrier = () => {
    const name = newName.trim();
    if (!name) return;
    run(async () => {
      const res = await fetch(`${apiUrl}/api/compliance/reference/carriers`, { method: 'POST', headers, body: JSON.stringify({ name }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to add carrier');
      setNewName('');
    });
  };

  const saveRename = (id) => {
    const name = editName.trim();
    if (!name) return;
    run(async () => {
      const res = await fetch(`${apiUrl}/api/compliance/reference/carriers/${id}`, { method: 'PUT', headers, body: JSON.stringify({ name }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Rename failed');
      setEditingId(null);
    });
  };

  const remove = (c) => {
    const msg = c.agents_count
      ? `Remove "${c.name}" from the master list? It will also be removed from ${c.agents_count} agent(s).`
      : `Remove "${c.name}" from the master list?`;
    if (!window.confirm(msg)) return;
    run(async () => {
      const res = await fetch(`${apiUrl}/api/compliance/reference/carriers/${c.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Delete failed');
    });
  };

  return (
    <Card className="mt-8" data-testid="admin-carrier-master-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5 text-[#0077B6]" />
          Master Carrier List
          <span className="text-sm font-normal text-gray-500">— carriers Utmost Financial Inc. holds contracts with ({carriers.length})</span>
        </CardTitle>
        <p className="text-sm text-gray-500">This list appears on every agent portal so advisors can tick the carriers they are contracted with.</p>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-red-600 mb-3" data-testid="carrier-master-error">{error}</p>}

        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCarrier(); } }}
            placeholder="Add a carrier (e.g., Wawanesa Life)…"
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0077B6]"
            data-testid="admin-add-carrier-input"
          />
          <Button onClick={addCarrier} disabled={busy || !newName.trim()} className="bg-[#0077B6]" data-testid="admin-add-carrier-btn">
            <Plus className="h-4 w-4 mr-1" />Add
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="admin-carrier-master-grid">
            {carriers.map((c) => (
              <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-white" data-testid={`carrier-row-${c.id}`}>
                {editingId === c.id ? (
                  <>
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveRename(c.id); if (e.key === 'Escape') setEditingId(null); }}
                      className="flex-1 px-2 py-1 border rounded text-sm"
                      data-testid={`carrier-rename-input-${c.id}`}
                    />
                    <button onClick={() => saveRename(c.id)} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Save" data-testid={`carrier-rename-save-${c.id}`}><Check className="h-4 w-4" /></button>
                    <button onClick={() => setEditingId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Cancel" data-testid={`carrier-rename-cancel-${c.id}`}><X className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm truncate" data-testid={`carrier-name-${c.id}`}>{c.name}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap" title="Agents holding this carrier">{c.agents_count} agent{c.agents_count === 1 ? '' : 's'}</span>
                    <button onClick={() => { setEditingId(c.id); setEditName(c.name); }} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Rename" data-testid={`carrier-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(c)} disabled={busy} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Remove" data-testid={`carrier-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCarrierMaster;
